import * as React from "react";
import { Fun, mk_widget, Unit, Widget } from "widgets-for-react";

type styleguidePages = 'about' | 'guidelines' | 'tokens' | 'objects' | 'components' | 'compositions'

type styleguideChapterOpts = {
  type: styleguidePages
  slug: string
  title: string
  intro?: string
  extraClasses?: string
}

export const styleguideChapter: Fun<styleguideChapterOpts, Fun<Widget<Unit>, Widget<Unit>>> = o => w => mk_widget({
  run: _ => (
    <section className={`styleguide-chapter styleguide-chapter--${o.type} ${o.extraClasses ? o.extraClasses : '' }`}>
      <span id={o.slug} className="styleguide__anchor" aria-hidden="true"></span>

      <header className="styleguide--styled styleguide-chapter__header">
        {o.title && <h2 className="styleguide__title styleguide-chapter__title">{o.title}</h2>}
        {o.intro && <p className="styleguide-chapter__intro">{o.intro}</p>}
      </header>

      <div className="styleguide-chapter__content">
        {w.run(_ => null)}
      </div>
    </section>
  )
})

export const styleguideChapterSimple: Fun<styleguideChapterOpts, Widget<Unit>> = o => mk_widget({
  run: _ => (
    <section className={`styleguide-chapter styleguide-chapter--${o.type} ${o.extraClasses ? o.extraClasses : '' }`}>
      <span id={o.slug} className="styleguide__anchor" aria-hidden="true"></span>

      <header className="styleguide--styled styleguide-chapter__header">
        {o.title && <h2 className="styleguide__title styleguide-chapter__title">{o.title}</h2>}
        {o.intro && <p className="styleguide-chapter__intro">{o.intro}</p>}
      </header>

    </section>
  )
})

type styleguideSectionOpts = {
  type: styleguidePages
  slug: string
  title: string
  intro?: string
  extraClasses?: string
}

export const styleguideSection: Fun<styleguideSectionOpts, Fun<Widget<Unit>, Widget<Unit>>> = o => w => mk_widget({
  run: _ => (
    <section className={`styleguide-section styleguide-section--${o.type} ${o.extraClasses ? o.extraClasses : '' }`}>
      <span id={o.slug} className="styleguide__anchor" aria-hidden="true"></span>
      
      <header className="styleguide--styled styleguide-section__header">
        {o.title && <h3 className="styleguide-section__title">{o.title}</h3>}
        {o.intro && <p className="styleguide-section__intro">{o.intro}</p>}
      </header>

      <div className="styleguide-section__content">
        {w.run(_ => null)}
      </div>
    </section>
  )
})

type styleguidePartOpts = {
  title?: string
  intro?: string
  extraClasses?: string
}

export const styleguidePart: Fun<styleguidePartOpts, Fun<Widget<Unit>, Widget<Unit>>> = o => w => mk_widget({
  run: _ => (
    <section className={`styleguide-part ${o.extraClasses ? o.extraClasses : '' }`}>
      <header className="styleguide--styled styleguide-part__header">
        {o.title && <h4 className="styleguide-part__title">{o.title}</h4>}
      </header>

      <div className="styleguide-part__content">
        {o.intro && 
          <div className="styleguide--styled styleguide-part__intro">
            <p className="styleguide-part__description">{o.intro}</p>
          </div>
        }
        {w.run(_ => null)}
      </div>
    </section>
  )
})

type styleguideTokenPartOpts = {
  hasExampleColumn: boolean
  hasAttributeColumn: boolean
  hasDescriptionColumn?: boolean
  extraClasses?: string
  exampleIsLarger?: boolean
}

export const styleguideTokenPart: Fun<styleguideTokenPartOpts, Fun<Widget<Unit>, Widget<Unit>>> = o => w => mk_widget({
  run: _ => (
    <div className="styleguide-table">
      <table className={`styleguide-table__inner ${o.extraClasses ? o.extraClasses : '' }`}>
        <thead>
          <tr className={`styleguide-table__heading${(o.hasExampleColumn == false || o.hasAttributeColumn == false) ? " styleguide-table__heading--2-column" : ''} styleguide--styled`}>
            <th className="styleguide-table__label styleguide-table__label--token">Token</th>
            {o.hasExampleColumn == true && <th className={`styleguide-table__label styleguide-table__label--example ${o.exampleIsLarger === true ? " styleguide-table__label--xl" : ''}`}>Example</th>}
            {o.hasAttributeColumn == true && <th className="styleguide-table__label styleguide-table__label--attributes">Attributes</th>}
            {o.hasDescriptionColumn == true && <th className="styleguide-table__label styleguide-table__label--description">Description</th>}
          </tr>
        </thead>

        <tbody>
          {w.run(_ => null)}
        </tbody>
      </table>
    </div>
  )
})

type styleguideExampleOpts = {
  showExampleLabel: boolean
  isFullScreen: boolean
  extraClasses?: string
  description?: string
}

export const styleguideExample: Fun<styleguideExampleOpts, Fun<Widget<Unit>, Widget<Unit>>> = o => w => mk_widget({
  run: _ => (
    <>
    <section className={`styleguide-example ${o.extraClasses ? o.extraClasses : '' }`}>
      {o.showExampleLabel == true &&
        <h5 className="styleguide-example__title">Example(s)</h5>
      }
      <div className="styleguide-example__section cf">
        {o.isFullScreen == true ? (
          <div className="fs">
            <div className="fs__inner">
              {w.run(_ => null)}
            </div>
          </div>
        ) : (
          w.run(_ => null)
        )}
      </div>
    </section>

    { o.description &&
      <section className="styleguide-description">
        <h5 className="styleguide-description__title">Description</h5>
        <div>
          <p className="styleguide-description__copy">{o.description}</p>
        </div>
      </section>
    }
    </>
  )
})

type styleguideTokenRowOpts = {
  code: string
  description?: string
  attributes?: string | Array<string>
  columnCount: number
  hasSeperator: boolean
  hasLabel: boolean
  label?: string 
  hasExample: boolean
}

export const styleguideTokenRow: Fun<styleguideTokenRowOpts, Fun<Widget<Unit>, Widget<Unit>>> = o => w => mk_widget({
  run: _ => (
    <>
      {o.hasLabel == true && o.label &&
        <tr className="styleguide-table__row styleguide-table__row--title">
          <td className="styleguide-table__cell styleguide--styled" colSpan={o.columnCount}>
            <span className="styleguide-token__color">{o.label}</span>
          </td>
        </tr>
      }
      <tr className="styleguide-table__row">
        <td className="styleguide-table__cell styleguide--styled">
          <code className="styleguide-token__code">{o.code}</code>
        </td>
        {o.hasExample == true &&
          <td className="styleguide-table__cell">
            {w.run(_ => null)}
          </td>
        }
        {o.attributes &&
          <td className="styleguide-table__cell styleguide--styled">
            {Array.isArray(o.attributes) ?
              o.attributes.map((item, key) => {
                return <span className="styleguide-token__attribute" key={key}>{item}</span>
              })
            : (
              <span className="styleguide-token__attribute">{o.attributes}</span>
            )}
          </td>
        }
        {o.description &&
          <td className="styleguide-table__cell styleguide--styled">
            {o.description &&<p className="styleguide-token__description">{o.description}</p>}
          </td>
        }
      </tr>

      {o.hasSeperator == true && (
        <tr className="styleguide-table__row" aria-hidden="true"><td className="styleguide-table__seperator" colSpan={o.columnCount}></td></tr>
      )}
    </>
  )
})

type styleguideCodeOpts = {
}

export const styleguideCode: Fun<styleguideCodeOpts, Fun<Widget<Unit>, Widget<Unit>>> = o => w => mk_widget({
  run: _ => (
    <section className="styleguide-code">
      <h5 className="styleguide-code__title">Code</h5>
      <div className="styleguide-code__section cf">
        {w.run(_ => null)}
      </div>
    </section>
  )
})

type StyleguidePlaceholderShapeProps = {
  width: number
  height: number
  radius: string
  bgColor: string
}

type StyleguidePlaceholderTextProps = {
  color: string
  font?: string
}

type StyleguidePlaceholderProps = {
  shape: StyleguidePlaceholderShapeProps,
  text?: StyleguidePlaceholderTextProps
}

// HOW TO USE: <img src={styleguidePlaceholder({ shape: { width: 200, height: 75, radius: '0', bgColor: '#E8E8E8' }, text: { color: '#1e1e1e', font: 'Open Sans, sans-serif' } })} ... />
export function StyleguideImgPlaceholder(p: StyleguidePlaceholderProps): string {
  return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="${p.shape.width}" height="${p.shape.height}" viewBox="0 0 ${p.shape.width} ${p.shape.height}"><rect rx="${p.shape.radius}" width="${p.shape.width}" height="${p.shape.height}" x="0" y="0" fill="${encodeURIComponent(p.shape.bgColor)}"/>${p.text && (`<text style="color: ${encodeURIComponent(p.text.color)}; font-size: ${(p.shape.width * .075)}px;${p.text.font && (` font-family: ${p.text.font};`)}" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">${p.shape.width}x${p.shape.height}</text>`)}</svg>`
}
